import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './auth/store';
import { fetchMe, ApiError, setSessionExpiredHandler } from './api/client';
import { RequireAuth } from './components/RequireAuth';
import { AppShell } from './components/AppShell';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ProjectDetailPage } from './pages/ProjectDetailPage';
import { ClientsPage } from './pages/ClientsPage';
import { ClientDetailPage } from './pages/ClientDetailPage';
import { NewClientPage } from './pages/NewClientPage';
import { NewProjectPage } from './pages/NewProjectPage';
import { EvidencePage } from './pages/EvidencePage';
import { NewEvidencePage } from './pages/NewEvidencePage';
import { EvidenceDetailPage } from './pages/EvidenceDetailPage';
import { TimeEntriesPage } from './pages/TimeEntriesPage';
import { NewTimeEntryPage } from './pages/NewTimeEntryPage';
import { InvoicesPage } from './pages/InvoicesPage';
import { NewInvoicePage } from './pages/NewInvoicePage';
import { InvoiceDetailPage } from './pages/InvoiceDetailPage';
import { ExpensesPage } from './pages/ExpensesPage';
import { NewExpensePage } from './pages/NewExpensePage';
import { ExpiringPage } from './pages/ExpiringPage';
import { AssetsPage } from './pages/AssetsPage';
import { ContractorsPage } from './pages/ContractorsPage';
import { VendorsPage } from './pages/VendorsPage';
import { StaffPage } from './pages/StaffPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { DocumentsPage } from './pages/DocumentsPage';
import { ServiceRatesPage } from './pages/ServiceRatesPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { ContractorPaymentsPage } from './pages/ContractorPaymentsPage';
import { CalendarPage } from './pages/CalendarPage';
import { SettingsPage } from './pages/SettingsPage';
import { UserSettingsPage } from './pages/UserSettingsPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { ReportsPage } from './pages/ReportsPage';
import { InvitationsPage } from './pages/InvitationsPage';
import { AcceptInvitationPage } from './pages/AcceptInvitationPage';
import { DirectoryPage } from './pages/DirectoryPage';
import { OrganizationDetailPage } from './pages/OrganizationDetailPage';
import { NewOrganizationPage } from './pages/NewOrganizationPage';
import { PersonDetailPage } from './pages/PersonDetailPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, err) => {
        // Don't retry auth failures or client errors
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

// A failed refresh means the session is over: drop the cached data (stops
// the notifications poll) and clear `me` so RequireAuth bounces to /login.
setSessionExpiredHandler(() => {
  if (!useAuthStore.getState().me) return;
  queryClient.clear();
  useAuthStore.getState().setMe(null);
});

export function App(): JSX.Element {
  const setMe = useAuthStore((s) => s.setMe);
  const setLoading = useAuthStore((s) => s.setLoading);

  // Bootstrap: fetch /me on app load. The endpoint always returns 200,
  // performing an inline refresh-cookie rotation if a session exists
  // (eliminates the 401 console noise on cold load). `me.user === null`
  // means no session — we drop to /login. Otherwise we hydrate the
  // auth store with the full session payload.
  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    fetchMe(ac.signal)
      .then(({ user, ...rest }) => setMe(user ? { user, ...rest } : null))
      .catch(() => setMe(null));
    return () => ac.abort();
  }, [setMe, setLoading]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* The public marketing site now lives in packages/site
              (served at stfors.com). This dashboard host's apex just
              routes into the app; RequireAuth bounces anonymous visitors
              to /login. */}
          <Route path="/" element={<Navigate to="/app" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/invite/:token" element={<AcceptInvitationPage />} />
          <Route
            element={
              <RequireAuth>
                <AppShell />
              </RequireAuth>
            }
          >
            <Route path="/app" element={<DashboardPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/new" element={<NewProjectPage />} />
            <Route path="/projects/:id" element={<ProjectDetailPage />} />
            <Route path="/directory" element={<DirectoryPage />} />
            <Route path="/organizations/new" element={<NewOrganizationPage />} />
            <Route path="/organizations/:id" element={<OrganizationDetailPage />} />
            <Route path="/people/:id" element={<PersonDetailPage />} />
            <Route path="/clients" element={<ClientsPage />} />
            <Route path="/clients/new" element={<NewClientPage />} />
            <Route path="/clients/:id" element={<ClientDetailPage />} />
            <Route path="/evidence" element={<EvidencePage />} />
            <Route path="/evidence/new" element={<NewEvidencePage />} />
            <Route path="/evidence/:id" element={<EvidenceDetailPage />} />
            <Route path="/time-entries" element={<TimeEntriesPage />} />
            <Route path="/time-entries/new" element={<NewTimeEntryPage />} />
            <Route path="/invoices" element={<InvoicesPage />} />
            <Route path="/invoices/new" element={<NewInvoicePage />} />
            <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/expenses/new" element={<NewExpensePage />} />
            <Route path="/expiring" element={<ExpiringPage />} />
            <Route path="/assets" element={<AssetsPage />} />
            <Route path="/contractors" element={<ContractorsPage />} />
            <Route path="/vendors" element={<VendorsPage />} />
            <Route path="/staff" element={<StaffPage />} />
            <Route path="/templates" element={<TemplatesPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/service-rates" element={<ServiceRatesPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/contractor-payments" element={<ContractorPaymentsPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            {/* Personal, user-scoped settings (gear/username in the topbar). */}
            <Route path="/settings" element={<UserSettingsPage />} />
            {/* Application/system settings hub (sidebar "Settings", admin-gated). */}
            <Route path="/system-settings" element={<SettingsPage />} />
            <Route path="/audit" element={<AuditLogPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/invitations" element={<InvitationsPage />} />
            {/* Legacy URL — recurring invoices live under Invoices → Repeating rules. */}
            <Route path="/recurring-invoices" element={<Navigate to="/invoices?view=rules" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/app" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
